import {
  AspectRatio,
  Badge,
  Box,
  Card,
  CardBody,
  Heading,
  HStack,
  Icon,
  Image,
  Stack,
  Text,
} from '@chakra-ui/react'
import { Bed, ShowerHead, Users } from 'lucide-react'
import React, { useMemo } from 'react'
import type { Property } from '../../../../types'

interface PropertyCardUIProps {
  property: Property
  onClick: () => void
  onEdit?: (property: Property) => void
  onDelete?: (property: Property) => void
  onRestore?: (property: Property) => void
  isDeleted?: boolean
  inLine: boolean
}

const PropertyCardUI: React.FC<PropertyCardUIProps> = ({
  property,
  onClick,
  isDeleted,
  inLine = false,
}) => {
  const mainImageSrc = useMemo(() => {
    if (property.images && property.images.length > 0) {
      return (
        property.images.find((img) => img.is_primary)?.url ||
        property.images[0].url
      )
    }
    return property.stays_main_image || undefined
  }, [property])

  return (
    <Card
      overflow="hidden"
      variant="outline"
      boxShadow="md"
      cursor="pointer"
      transition="transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
      width="100%"
      position="relative"
      className={`${inLine ? 'md:flex-row' : ''}`}
    >
      <Box position="relative" className={`${inLine ? 'h-full w-32' : ''}`}>
        <AspectRatio
          ratio={{ base: 16 / 9, sm: 4 / 3 }}
          width="100%"
          className={`${inLine ? 'h-full' : ''}`}
        >
          <Image
            src={mainImageSrc}
            alt={property.title || 'Property Image'}
            fallbackSrc="https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800"
            objectFit="cover"
            height="100%"
            width="100%"
          />
        </AspectRatio>
        <HStack position="absolute" top="4" right="4" spacing="2">
          {isDeleted && (
            <Badge
              colorScheme="red"
              variant="solid"
              px={3}
              py={1}
              borderRadius="full"
            >
              Excluído
            </Badge>
          )}
          <Badge
            colorScheme={property.status === 'active' ? 'green' : 'red'}
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
          >
            {property.status === 'active' ? 'Ativo' : property.status}
          </Badge>
        </HStack>
      </Box>

      <CardBody
        as={Stack}
        p={{ base: 4, md: 5 }}
        spacing={4}
        flexGrow={1}
        onClick={onClick}
      >
        <Box>
          <HStack justify="space-between" align="flex-start">
            <Box flex="1">
              <Heading as="h3" size="md" noOfLines={2} fontWeight="medium">
                {property.title}
              </Heading>
              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                {property.address &&
                  `${property.address.street}, ${property.address.streetNumber} -
                ${property.address.region} / ${property.address.city}`}
              </Text>
            </Box>
            <HStack spacing={2}>
              <Text color="blue.600" fontWeight="semibold" fontSize="sm">
                {property.code}
              </Text>
            </HStack>
          </HStack>
        </Box>

        <HStack spacing={4}>
          <HStack>
            <Icon as={Bed} color="gray.500" />
            <Text color="gray.600" fontSize="sm">
              {property.bedrooms}
            </Text>
          </HStack>
          <HStack>
            <Icon as={ShowerHead} color="gray.500" />
            <Text color="gray.600" fontSize="sm">
              {property.bathrooms}
            </Text>
          </HStack>
          <HStack>
            <Icon as={Users} color="gray.500" />
            <Text color="gray.600" fontSize="sm">
              {property.max_guests}
            </Text>
          </HStack>
        </HStack>
      </CardBody>
    </Card>
  )
}

export default PropertyCardUI
